import * as Joi from "joi-browser";
import { MdInfoOutline } from "react-icons/md";
import freeEmailDomains from "free-email-domains";
import { parsePhoneNumberFromString } from "libphonenumber-js";
import moment from "moment";
import tickIcon from "../assets/img/tickIcon.png";
import docsData from "../Utils/docsData";
import constants, { DEA_OVERLAY } from "../Utils/constants";
import axiosInstance from "../services/authWrapper";
import { CustomToast } from "../components/CustomToast";
import { toast as mainToast } from "react-toastify";
import streetNameMappings from "../Utils/streetNameMappings.json";

export const haveValue = (val) => {
  if (
    val !== "" &&
    val !== "undefined" &&
    val !== undefined &&
    val !== null &&
    val !== "null"
  ) {
    return true;
  } else {
    return false;
  }
};

const cleanAddressPart = (value = "") => {
  if (!haveValue(value)) return "";

  return toTitleCase(value)
    .replace(/[,;:/\\|_-]+/g, " ")
    .replace(/[^a-zA-Z0-9À-ÿ\s]/g, "")
    .replace(/\s+/g, " ")
    .trim();
};

const getStreetTypeMapping = (streetType) => {
  if (!haveValue(streetType)) return undefined;

  const streetTypeValue = toLowerCase(streetType)
    .replace(/[,;:/\\|_-]+/g, "")
    .trim();

  return streetNameMappings.find((mapping) => {
    const values = [
      mapping.spanish_name,
      mapping.english_name,
      mapping.es_abbr,
      mapping.en_abbr,
    ];

    return values.some(
      (value) =>
        haveValue(value) &&
        toLowerCase(value)
          .replace(/[,;:/\\|_-]+/g, "")
          .trim() === streetTypeValue
    );
  });
};

const getAddressLanguage = (language = "es") =>
  toLowerCase(language || "es").startsWith("es") ? "es" : "en";

const formatStreetNumber = (streetNumber) => {
  if (!haveValue(streetNumber)) return "";

  const parsedStreetNumber = parseInt(streetNumber, 10);
  return Number.isNaN(parsedStreetNumber) ? "" : String(parsedStreetNumber);
};

const formatFloorNumber = (floorNumber) => {
  if (!haveValue(floorNumber)) return "";

  const parsedFloorNumber = parseInt(floorNumber, 10);
  if (Number.isNaN(parsedFloorNumber)) return cleanAddressPart(floorNumber);

  return String(parsedFloorNumber).padStart(2, "0");
};

export const formatPropertyAddress = (property = {}, language = "es") => {
  const addressLanguage = getAddressLanguage(language);
  const street = property?.street?.toString().trim() || "";
  const firstStreetToken = street.split(/[\s,;:/\\|_-]+/)[0] || "";
  const firstTokenStreetTypeMapping = getStreetTypeMapping(firstStreetToken);
  const streetTypeMapping =
    getStreetTypeMapping(property?.street_type) || firstTokenStreetTypeMapping;
  const shouldRemoveStreetTypeFromName = Boolean(firstTokenStreetTypeMapping);
  const streetTypeAbbr =
    streetTypeMapping?.[addressLanguage === "es" ? "es_abbr" : "en_abbr"];
  const streetName = cleanAddressPart(
    shouldRemoveStreetTypeFromName
      ? street.replace(
          new RegExp(`^${firstStreetToken}[\\s,;:/\\\\|_-]*`, "i"),
          ""
        )
      : street
  );
  const streetNumber = formatStreetNumber(property?.street_number);
  const floorNumber = formatFloorNumber(property?.floor_number);
  const entrance = cleanAddressPart(property?.staircase);
  const door = cleanAddressPart(property?.flat_number);
  const prefixes =
    addressLanguage === "es"
      ? { entrance: "Es:", floor: "Pl:", door: "Pt:" }
      : { entrance: "En:", floor: "Fl:", door: "Dr:" };

  return [
    streetTypeAbbr,
    streetName,
    streetNumber,
    entrance ? `${prefixes.entrance}${entrance}` : "",
    floorNumber ? `${prefixes.floor}${floorNumber}` : "",
    door ? `${prefixes.door}${door}` : "",
  ]
    .filter(haveValue)
    .join(" ");
};

export const withBackendUrl = (path, defaultPath = null) => {
  if (typeof path === "object") {
    let src = URL.createObjectURL(path);
    return src;
  } else {
    if (haveValue(path)) {
      path = path?.toString();
      if (path?.startsWith("http://") || path?.startsWith("https://")) {
        return path;
      } else {
        if (path.startsWith("/")) {
          path = path.substring(1);
        }
        return process.env.REACT_APP_SERVER_URL + "/" + path;
      }
    } else {
      return "";
    }
  }
};
export const toLowerCase = (data) => {
  return data.toString().toLowerCase();
};

export const convertCurrencyFormat = (amount) => {
  return new Intl.NumberFormat(localStorage.getItem("i18nextLng"), {
    currency: localStorage.getItem("i18nextLng") === "es" ? "EUR" : "USD",
  }).format(amount);
};

export const toUpperCase = (data) => {
  return data.toString().toUpperCase();
};

export const toTitleCase = (value = "") => {
  if (!value) return "";

  return value
    .toString()
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim()
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

export const getUserDisplayName = (user = {}, fallback = "N/A") => {
  const docData = user?.onfido_user_kyc?.onfido_doc_data;
  const firstName = docData?.first_name;
  const lastName = docData?.last_name ?? "";

  if (haveValue(firstName)) {
    return toTitleCase(`${firstName} ${lastName}`);
  }

  if (haveValue(user?.forensic_name)) {
    return toTitleCase(user?.forensic_name);
  }

  return `${user?.fname ?? fallback} ${user?.lname ?? ""}`.trim();
};

export const getNotificationDisplayText = (notification = {}, _t) => {
  const translate = typeof _t === "function" ? _t : (key) => key;
  const tenantName = getUserDisplayName(notification?.created_by_tenant, "");

  switch (notification?.notification_type) {
    case "guarantor_removed": {
      const [firstName = "", lastName = ""] = (notification?.body ?? "").split(
        " "
      );
      return `${firstName} ${lastName} ${translate(
        "hasRemovedHimselfAsAGuarantorFor"
      )} ${notification?.shared_profile_id?.tenant_fname ?? ""} ${
        notification?.shared_profile_id?.tenant_lname ?? ""
      }`.trim();
    }

    case "guarantor_declined":
      return `${translate("guarantorRequestDeclinedBy")} ${tenantName}`.trim();

    case "property_assigned":
      return `${
        notification?.created_by_landlord?.fname_companyName ?? ""
      } ${translate("hasAwardedYouAProperty")}`.trim();

    case "profile_linked":
      return `${
        notification?.created_by_landlord?.fname_companyName ?? ""
      } ${translate("hasLinkedYourProfileToAAddress")}.`.trim();

    case "profile_sharing":
      return `${tenantName} ${translate("hasSharedProfileWithYou")}`.trim();

    case "profile_sharing_accepted":
      return `Tenant ${tenantName} ${translate("wasSharedWithWS")} ${
        notification?.workspaceId?.name ?? ""
      }`.trim();

    case "document_updated":
      return `${tenantName} ${translate("hasUpdatedTheirDocuments")}`.trim();

    case "guarantor_document_updated":
      return `${tenantName}'s ${translate("guarantor")} ${translate(
        "hasUpdatedTheirDocuments"
      )}`.trim();

    case "guarantor_added":
      return `${tenantName} ${translate("hasAddedNewGuarantor")}`.trim();

    case "financial_info_updated":
      return `${tenantName} ${translate(
        "hasUpdatedTheirFinancialInformation"
      )}`.trim();

    case "financial_info_updated_guarantor":
      return `${tenantName}'s guarantor ${translate(
        "hasUpdatedTheirFinancialInformation"
      )}`.trim();

    case "profile_sharing_request":
      return `${tenantName} ${translate(
        "hasRequestedSharedProfileWithYou"
      )}`.trim();

    default:
      return notification?.body ?? "";
  }
};

export const validateJOIFormField = (formFields, schema) => {
  const result = Joi.validate(formFields, schema, {
    abortEarly: false,
    stripUnknown: true,
  });
  const { error } = result;
  if (!error) {
    return null;
  } else {
    const errorData = {};
    for (let item of error.details) {
      const name = item.path[0];
      errorData[name] = item.message;
    }
    return errorData;
  }
};

export const validateJOIProperty = (schema, event) => {
  const { name, value } = event.target;
  const obj = { [name]: value };
  if (schema.hasOwnProperty(name)) {
    const subSchema = { [name]: schema[name] };
    const result = Joi.validate(obj, subSchema, {
      stripUnknown: true,
    });
    const { error } = result;
    return error ? error.details[0].message : null;
  } else {
    return null;
  }
};

export const renderErrorMessage = (errors, type) => {
  if (errors && errors[type]) {
    return errors[type];
  }
  return "";
};

export const getBaseUrl = () => {
  const baseUrl = `${window.location.protocol}//${window.location.host}`;
  return baseUrl;
};

export const truncateString = (str, maxLength = 100) => {
  if (str.length > maxLength) {
    return str.substring(0, maxLength) + "...";
  }
  return str;
};

export const skipMaccUser = (user_type = null) => {
  switch (user_type) {
    case "macc":
      return true;
    default:
      return false;
  }
};

export const verifyDniNie = (user, nie_val) => {
  var result = false;
  if (user?.onfido_kyc_success) {
    if (user?.onfido_user_kyc?.onfido_doc_data?.personal_number == nie_val) {
      result = true;
    }
  }
  if (result == false) {
    if (user?.dni_nie == nie_val) {
      result = true;
    }
  }
  return result;
};

function getMonthKey(monthNumber) {
  const months = [
    "january",
    "february",
    "march",
    "april",
    "may",
    "june",
    "july",
    "august",
    "september",
    "october",
    "november",
    "december",
  ];
  return months[monthNumber - 1];
}

const normalizeSalary = (value) => {
  if (typeof value === "string") {
    value = value.replace(/,/g, ""); // Remove commas
  }
  return Math.ceil(parseFloat(value));
};

export const toCamelCase = (str) => {
  return str
    .replace(/[^\w\s]/g, "")
    .replace(/(?:^\w|[A-Z]|\b\w|\s+)/g, (match, index) =>
      index === 0 ? match.toLowerCase() : match.toUpperCase()
    )
    .replace(/\s+/g, "");
};

export const validatePhoneNumber = (fullPhoneNumber) => {
  try {
    const phoneNumberWithPlus = `+${fullPhoneNumber}`;
    const phoneNumberObject = parsePhoneNumberFromString(phoneNumberWithPlus);
    if (phoneNumberObject && phoneNumberObject.isValid()) {
      return { isValid: true, message: "Valid phone number" };
    }
    return { isValid: false, message: "Invalid phone number." };
  } catch (error) {
    return { isValid: false, message: "Invalid phone number" };
  }
};

export const calculateEffortRate = (user_income, user, profile_data) => {
  if (haveValue(user_income) && haveValue(user)) {
    const percentage = haveValue(profile_data?.rent_percentage)
      ? profile_data?.rent_percentage
      : user?.rent_percentage;
    const effortAmount = (user_income * (percentage ?? 0)) / 100;
    return {
      annual_sal: user_income * 12,
      monthly_sal: user_income,
      effort_rate: percentage ?? 0,
      effort_amount: parseFloat(effortAmount.toFixed(2)),
    };
  } else {
    return {
      annual_sal: "N/A",
      monthly_sal: "N/A",
      effort_rate: "N/A",
      effort_amount: "N/A",
    };
  }
};

export const calculateEffortRatePercentageTenant = (
  user_income,
  user,
  custom_percentage,
  llShowOnly
) => {
  if (haveValue(user)) {
    const percentage =
      llShowOnly || custom_percentage
        ? custom_percentage
        : user?.effort_rate_percentage;

    const effortAmount = (user_income * (percentage ?? 35)) / 100;
    return {
      annual_sal: user_income * 12,
      monthly_sal: user_income,
      effort_rate: percentage ?? 0,
      effort_amount: parseFloat(effortAmount.toFixed(2)),
    };
  } else {
    return {
      annual_sal: 0,
      monthly_sal: 0,
      effort_rate: 0,
      effort_amount: 0,
    };
  }
};

export const calculateEffortRatePercentageLandlord = (
  user_income,
  user,
  custom_percentage
) => {
  if (haveValue(user)) {
    const percentage = custom_percentage
      ? custom_percentage
      : user?.effort_rate_percentage;
    const effortAmount = (user_income * (percentage ?? 0)) / 100;
    return {
      annual_sal: user_income * 12,
      monthly_sal: user_income,
      effort_rate: percentage ?? 0,
      effort_amount: parseFloat(effortAmount.toFixed(2)),
    };
  } else {
    return {
      annual_sal: 0,
      monthly_sal: 0,
      effort_rate: 0,
      effort_amount: 0,
    };
  }
};

export const DEBUG_CALCULATION = false;

export const getRentPercentage = (income, rent) => {
  const cleanIncome = Number(income);
  const cleanRent = Number(rent);

  if (cleanIncome === 0) {
    if (DEBUG_CALCULATION) {
      console.log(
        "[getRentPercentage] income=0, rent=",
        cleanRent,
        "→ returning",
        cleanRent > 0 ? "100 (hard-fail)" : "0"
      );
    }
    if (cleanRent > 0) return { exactValue: Infinity, roundValue: 100 };
    return { exactValue: 0, roundValue: 0 };
  }

  const percentage = (cleanRent / cleanIncome) * 100;

  return {
    exactValue: (percentage * 100) / 100,
    roundValue: Math.round((percentage * 100) / 100),
  };
};

export function nl2br(str) {
  if (typeof str !== "string") return "";
  return str.replace(/\n/g, "<br>");
}

export function nl2ul(str, UlClass = "") {
  if (typeof str !== "string") return "";

  const items = str
    .split("\n")
    .map((item) => item.trim())
    .filter((item) => item.length > 0); // remove empty lines

  if (items.length === 0) return "";

  const listItems = items
    .map((item) => `<li class="report-list-style summery_listing">${item}</li>`)
    .join("");

  return `<ul class=${UlClass}>${listItems}</ul>`;
}

export const payslipValidityCheck = (payslip_data, user) => {
  if (
    haveValue(payslip_data) &&
    verifyDniNie(user, payslip_data?.data?.nieDniNumber)
  ) {
    const payslip = payslip_data?.data;
    const situaciones = user?.vida_laboral_id?.response?.situaciones || [];

    const empresaVida = situaciones[0]?.nombreEmpresa
      ?.split(" ")[0]
      ?.toLowerCase();
    const empresaPayslip = payslip?.employer?.split(" ")[0]?.toLowerCase();

    const isEmployerDataAvailable = !!empresaVida && !!empresaPayslip;
    const isEmployerMatch = empresaPayslip === empresaVida;

    const deductionData =
      user?.tenant_extra_field_id?.deduction_cert_data?.company_info || [];
    const payslipDate = payslip?.date;

    let isSalaryMatch = false;
    let isSalaryDataAvailable = false;

    if (payslipDate) {
      const [day, month, year] = payslipDate.includes("-")
        ? payslipDate.split("-").length === 3 &&
          payslipDate.split("-")[2].length === 4
          ? payslipDate.split("-")
          : payslipDate.split("-").reverse()
        : ["", "", ""];

      const monthKey = getMonthKey(parseInt(month));
      const targetYear = year;

      for (const company of deductionData) {
        const yearObj = company?.year;
        const salaryValue = yearObj?.[targetYear]?.[monthKey];

        if (salaryValue) {
          isSalaryDataAvailable = true;

          if (
            normalizeSalary(salaryValue) === normalizeSalary(payslip?.netSalary)
          ) {
            isSalaryMatch = true;
            break;
          }
        }
      }
    }

    const hasBothData = isEmployerDataAvailable && isSalaryDataAvailable;
    const hasAnyData = isEmployerDataAvailable || isSalaryDataAvailable;

    const isBothMatch = isEmployerMatch && isSalaryMatch;

    return !hasAnyData
      ? null
      : (hasBothData && isBothMatch) ||
          (!isEmployerDataAvailable &&
            isSalaryDataAvailable &&
            isSalaryMatch) ||
          (!isSalaryDataAvailable && isEmployerDataAvailable && isEmployerMatch)
        ? true
        : false;
  } else {
    return false;
  }
};

export const getRequiredPayslipPeriods = () => {
  const periods = [];
  const today = moment();

  for (let i = 1; i <= 3; i++) {
    const periodDate = today.clone().subtract(i, "months");
    periods.push({
      period_month: periodDate.month() + 1,
      period_year: periodDate.year(),
      monthName: periodDate.format("MMMM"),
      monthNumber: periodDate.month() + 1,
    });
  }

  return periods;
};

export const isPayslipWithinRequiredPeriod = (payslip, requiredPeriods) => {
  if (!payslip || !requiredPeriods) return false;

  const payslipPeriodYear = payslip?.period_year;
  const payslipPeriodMonth = payslip?.period_month;

  if (!payslipPeriodYear || !payslipPeriodMonth) {
    const payslipDate = payslip?.date || payslip?.created_at;
    if (payslipDate) {
      const parsedDate = moment(payslipDate);
      if (parsedDate.isValid()) {
        return requiredPeriods.some((period) => {
          const periodDate = moment()
            .year(period.period_year)
            .month(period.period_month - 1);
          return parsedDate.isSame(periodDate, "month");
        });
      }
    }
    return false;
  }

  return requiredPeriods.some(
    (period) =>
      period.period_year === payslipPeriodYear &&
      period.period_month === payslipPeriodMonth
  );
};

export const getPayslipDateWarning = (payslip, requiredPeriods, _t) => {
  if (!payslip || isPayslipWithinRequiredPeriod(payslip, requiredPeriods)) {
    return null;
  }

  const payslipPeriodYear = payslip?.period_year;
  const payslipPeriodMonth = payslip?.period_month;

  let payslipDate;
  if (payslipPeriodYear && payslipPeriodMonth) {
    payslipDate = moment()
      .year(payslipPeriodYear)
      .month(payslipPeriodMonth - 1);
  } else {
    payslipDate = moment(payslip?.date || payslip?.created_at);
  }

  if (!payslipDate?.isValid()) return null;

  const requiredMonths = requiredPeriods
    .map((p) =>
      moment()
        .year(p.period_year)
        .month(p.period_month - 1)
        .format("MMMM YYYY")
    )
    .join(", ");

  return _t
    ? _t("payslipTooOldWarning", "This payslip is from {date}")
        .replace("{date}", payslipDate.format("MMMM YYYY"))
        .replace("{requiredMonths}", requiredMonths)
    : `This payslip is from ${payslipDate.format("MMMM YYYY")}.`;
};

const validatePayslipMatch = (user, payslipKey) => {
  const payslipData = user?.tenant_extra_field_id?.payslip_data?.[payslipKey];
  return payslipValidityCheck(payslipData, user) === true;
};

export const showTickOnDoc = (docObject, user) => {
  if (!docObject) return false;

  if (docObject.from === "clave") return true;

  if (docObject.type === "Last annual Tax Report (Model 100)") return true;

  if (docObject.type === "national_identity_card_front") return true;

  if (docObject.type === "national_identity_card_back") return true;

  if (
    docObject.type === "1st month payslip" &&
    validatePayslipMatch(user, "payslip1")
  )
    return true;

  if (
    docObject.type === "2nd month payslip" &&
    validatePayslipMatch(user, "payslip2")
  )
    return true;

  if (
    docObject.type === "3rd month payslip" &&
    validatePayslipMatch(user, "payslip3")
  )
    return true;

  return false;
};

export const getCustomIncome = (
  user,
  landlordUser = null,
  _t,
  incomeSource
) => {
  let tenantExtraField = user?.tenant_extra_field_id;

  switch (incomeSource) {
    case "bank": {
      if (haveValue(user?.algoanIncome?.monthlyAmount)) {
        return {
          amount: Math.round(user.algoanIncome.monthlyAmount),
          method: _t("bank"),
          source: "bank",
        };
      }
      break;
    }

    case "payslip1": {
      const net = tenantExtraField?.payslip_data?.payslip1?.data?.netSalary;
      if (haveValue(net)) {
        return {
          amount: Math.round(net),
          method: _t("payslip"),
          source: "payslip1",
        };
      }
      break;
    }

    case "payslip2": {
      const net = tenantExtraField?.payslip_data?.payslip2?.data?.netSalary;
      if (haveValue(net)) {
        return {
          amount: Math.round(net),
          method: _t("payslip"),
          source: "payslip2",
        };
      }
      break;
    }

    case "payslip3": {
      const net = tenantExtraField?.payslip_data?.payslip3?.data?.netSalary;
      if (haveValue(net)) {
        return {
          amount: Math.round(net),
          method: _t("payslip"),
          source: "payslip3",
        };
      }
      break;
    }

    case "declaredIncome": {
      if (haveValue(user?.current_annual_income)) {
        return {
          amount: Math.round((user.current_annual_income ?? 0) / 12),
          method: _t("declaredIncome"),
          source: "declaredIncome",
        };
      }
      break;
    }

    default: {
      return getIncomeForEffortRate(user, landlordUser, _t);
    }
  }

  // If none of the cases returned (because data wasn't valid), fallback
  return getIncomeForEffortRate(user, landlordUser, _t);
};

const getAllIncomeSources = (user, originalUser, isDeaWorkspace = false) => {
  const netIncome = getUpdatedExchangeIncome(
    user?.current_annual_income ?? 0,
    user?.currency,
    originalUser
  );

  const forensicRaw =
    user?.tenant_extra_field_id?.forensic_financial_data ?? {};
  const forensicConverted = {};
  const kycDetails = getIdentityDetails(user, user?.onfido_user_kyc);
  const allReports =
    user?.tenant_extra_field_id?.forensic_analysis?.profileReports ?? [];
  let matchedReport;
  if (haveValue(kycDetails.value)) {
    matchedReport =
      allReports.find((report) =>
        report?.report?.keyFactors?.keyFactors?.some((kf) => {
          if (!kf?.dataInJSON || typeof kycDetails?.value !== "string")
            return false;

          let expectedKey;
          if (kycDetails.type?.toLowerCase() === "dni") {
            expectedKey = "dni";
          } else if (kycDetails.type?.toLowerCase() === "passport") {
            expectedKey = "passportNumber";
          } else {
            return false; // type not supported
          }

          const fieldValue = kf?.dataInJSON?.[expectedKey];
          return (
            typeof fieldValue === "string" &&
            fieldValue.toLowerCase() === kycDetails.value.toLowerCase()
          );
        })
      ) || allReports[0];
  } else {
    matchedReport = allReports[0];
  }
  const temp_data = matchedReport?.report;
  const temp_key = {};
  temp_data?.keyFactors?.keyFactors.map((val) => {
    temp_key[val.keyFactor] = val;
  });

  for (const key in forensicRaw) {
    const entry = forensicRaw[key];

    const amount = entry?.amount ?? 0;
    const currency = entry?.currency ?? "EUR";

    forensicConverted[key] = getUpdatedExchangeIncome(
      amount,
      currency,
      originalUser
    );
  }

  const isTargetEmployment =
    temp_key?.["Employment Type"]?.dataInJSON?.employmentType === "Employee" ||
    temp_key?.["Employment Type"]?.dataInJSON?.employmentType ===
      "Self-employed (Company Owner)" ||
    // Added fallback becauase in tenant listing we are not getting forensic data
    user?.employment_status === constants.employed ||
    (user?.employment_status == "self_employed" &&
      user?.employment_category === "ownACompany");

  if (
    originalUser?.current_workspace?.dea_rules_activated &&
    isDeaWorkspace &&
    isTargetEmployment
  ) {
    if (haveValue(forensicConverted?.netIncomeAfterDeductions)) {
      forensicConverted.itr = forensicConverted.netIncomeAfterDeductions;
    } else if (haveValue(forensicConverted?.itrIncomeAfterDeduction)) {
      forensicConverted.itr = forensicConverted.itrIncomeAfterDeduction;
    }
  }

  delete forensicConverted.netIncomeAfterDeductions;
  delete forensicConverted.itrIncomeAfterDeduction;

  return {
    _id: user?._id,
    name: getUserDisplayName(user, ""),
    declaredIncome: netIncome ?? 0,
    // #NOTE: not multiplying with 12, because we need monthly data
    bank: Math.round(user?.algoanIncome?.monthlyAmount ?? 0),
    ...forensicConverted,
    forensic_status: user?.forensic_status,
    user_email: user?.email,
    step: user?.step,
    isForensicExists: haveValue(
      user?.tenant_extra_field_id?.forensic_analysis?.profileReports
    ),
    profile_completed: user?.profile_completed,
    // #TODO: add here custom income sources
  };
};

export const getCustomIncomeDetails = (
  user,
  originalUser,
  _t,
  incomeSource,
  isTenant,
  needFallback = true,
  isDeaWorkspace = false,
  customIncomeData = null
) => {
  const llShowOnly =
    typeof window !== "undefined" &&
    (localStorage.getItem("usertype") === "landlord" ||
      localStorage.getItem("usertype") === "admin");
  const temp_all_sources = getAllIncomeSources(
    user,
    originalUser,
    isDeaWorkspace
  );
  const incomeSourceArray = Array.isArray(incomeSource)
    ? incomeSource
    : [incomeSource];
  const incomeSourceMode =
    incomeSourceArray
      .find((source) => source?.startsWith?.("__income_mode:"))
      ?.replace("__income_mode:", "") || "avg";

  let totalAmount = 0;
  let methods = [];
  let sources = [];

  const sourcesToCheck = incomeSourceArray.filter(
    (source) => !source?.startsWith?.("__income_mode:")
  );

  for (const source of sourcesToCheck) {
    switch (true) {
      case source === "bank": {
        if (
          haveValue(user?.algoanIncome?.monthlyAmount) &&
          user?.algoanIncome?.monthlyAmount > 0
        ) {
          totalAmount += Math.round(user?.algoanIncome?.monthlyAmount ?? 0);
          methods.push(_t("bank"));
          sources.push("bank");
        }
        break;
      }

      case source === "itr": {
        if (haveValue(temp_all_sources?.itr) && temp_all_sources?.itr > 0) {
          const monthAmount = (temp_all_sources?.itr ?? 0) / 12;
          totalAmount += Math.round(monthAmount);
          methods.push(_t("itr"));
          sources.push("itr");
        }
        break;
      }

      case source === "declaredIncome": {
        if (!llShowOnly) {
          const netIncome = getUpdatedExchangeIncome(
            user?.current_annual_income,
            user?.currency,
            originalUser
          );
          if (haveValue(netIncome) && netIncome > 0) {
            totalAmount += Math.round(netIncome ?? 0);
            methods.push(_t("declaredIncome"));
            sources.push("declaredIncome");
          }
        }
        break;
      }

      case source?.startsWith?.("payslip:"): {
        const payslipAmount = temp_all_sources?.[source];
        if (typeof payslipAmount === "number" && payslipAmount > 0) {
          totalAmount += payslipAmount;
          methods.push(_t("payslip"));
          sources.push(source);
        }
        break;
      }

      case source?.startsWith?.("custom_income:"): {
        const customIncomeDataActual =
          customIncomeData ||
          user?.custom_income_sources ||
          originalUser?.custom_income_sources;
        if (customIncomeDataActual) {
          const customIdentifier = source.replace("custom_income:", "");
          let allCustom = [];

          const userId = user?._id;
          if (customIncomeDataActual?.[userId]) {
            const entry = customIncomeDataActual[userId];
            const tenantSources = Array.isArray(entry?.tenant_source)
              ? entry.tenant_source
              : [];
            const guarantorSources = Array.isArray(
              entry?.guarantor_sources?.[userId]
            )
              ? entry.guarantor_sources[userId]
              : [];
            allCustom = [...tenantSources, ...guarantorSources];
          } else {
            for (const entry of Object.values(customIncomeDataActual)) {
              const tenantSources = Array.isArray(entry?.tenant_source)
                ? entry.tenant_source
                : [];
              const guarantorSources = Array.isArray(
                entry?.guarantor_sources?.[userId]
              )
                ? entry.guarantor_sources[userId]
                : [];
              allCustom = [...allCustom, ...tenantSources, ...guarantorSources];
            }
          }

          const found = allCustom.find((s) => s?.name === customIdentifier);
          if (found && typeof found.amount === "number" && found.amount > 0) {
            totalAmount += found.amount;
            methods.push(found.name || _t("customIncomeSource", "Custom"));
            sources.push(source);
          }
        }
        break;
      }
    }
  }

  if (totalAmount > 0) {
    const calculatedAmount =
      incomeSourceMode === "avg" && sources.length
        ? Math.round(totalAmount / sources.length)
        : totalAmount;

    return {
      amount: calculatedAmount,
      method: methods.join(", "),
      source: sources,
      source_mode: incomeSourceMode,
      all_sources: temp_all_sources,
    };
  }

  const fallbackIncomeDetails = getIncomeDetailsBasedOnHierarchy(
    user,
    _t,
    temp_all_sources,
    originalUser
  );

  return {
    ...fallbackIncomeDetails,
    source_mode: incomeSourceMode,
  };
};

export const getIncomeDetailsBasedOnHierarchy = (
  user,
  _t,
  temp_all_sources,
  originalUser
) => {
  const llShowOnly =
    typeof window !== "undefined" &&
    (localStorage.getItem("usertype") === "landlord" ||
      localStorage.getItem("usertype") === "admin");
  const payslipKey = Object.keys(temp_all_sources).find((k) =>
    k?.startsWith("payslip:")
  );
  if (payslipKey) {
    const payslipAmount = temp_all_sources[payslipKey];
    if (typeof payslipAmount === "number" && payslipAmount > 0) {
      return {
        amount: payslipAmount,
        method: _t("payslip"),
        source: [payslipKey],
        all_sources: temp_all_sources,
      };
    }
  }

  if (haveValue(temp_all_sources?.itr) && temp_all_sources?.itr > 0) {
    const monthAmount = (temp_all_sources?.itr ?? 0) / 12;
    return {
      amount: Math.round(monthAmount),
      method: _t("itr"),
      source: ["itr"],
      all_sources: temp_all_sources,
    };
  }

  if (
    haveValue(user?.algoanIncome?.monthlyAmount) &&
    user?.algoanIncome?.monthlyAmount > 0
  ) {
    return {
      amount: Math.round(user?.algoanIncome?.monthlyAmount ?? 0),
      method: _t("bank"),
      source: ["bank"],
      all_sources: temp_all_sources,
    };
  }

  const netIncome = getUpdatedExchangeIncome(
    user?.current_annual_income ?? 0,
    user?.currency,
    originalUser
  );

  if (!llShowOnly) {
    return {
      amount: netIncome,
      method: _t("declaredIncome"),
      source: ["declaredIncome"],
      all_sources: temp_all_sources,
    };
  }

  return {
    amount: 0,
    method: "",
    source: [],
    all_sources: temp_all_sources,
  };
};

export const getIncomeForEffortRateNew = (user, landlordUser = null, _t) => {
  if (
    haveValue(landlordUser) &&
    haveValue(landlordUser?.agency_id?.income_hierarchy)
  ) {
    return hierarchyBasedAmount(user, landlordUser, _t);
  } else {
    // bank
    if (haveValue(user?.algoanIncome?.monthlyAmount)) {
      return {
        amount: Math.round(user?.algoanIncome?.monthlyAmount),
        method: _t("bank"),
        source: "bank",
      };
    }

    // declared income
    if (haveValue(user?.current_annual_income)) {
      return {
        amount: Math.round((user?.current_annual_income ?? 0) / 12),
        method: _t("declaredIncome"),
        source: "declaredIncome",
      };
    }
    return {
      amount: 0,
      method: _t("noIncomeSource"),
      source: "noIncomeSource",
    };
  }
};
export const getIncomeForEffortRate = (user, landlordUser = null, _t) => {
  if (
    haveValue(landlordUser) &&
    haveValue(landlordUser?.agency_id?.income_hierarchy)
  ) {
    return hierarchyBasedAmount(user, landlordUser, _t);
  } else {
    let tenantExtraField = user?.tenant_extra_field_id;

    // bank
    if (haveValue(user?.algoanIncome?.monthlyAmount)) {
      return {
        amount: Math.round(user?.algoanIncome?.monthlyAmount),
        method: _t("bank"),
        source: "bank",
      };
    }

    // payslips
    if (
      haveValue(tenantExtraField?.payslip_data?.payslip1?.data?.netSalary) ||
      haveValue(tenantExtraField?.payslip_data?.payslip2?.data?.netSalary) ||
      haveValue(tenantExtraField?.payslip_data?.payslip3?.data?.netSalary)
    ) {
      //Get the value of latest Payslip
      let payslip_data = null;
      let source = null;
      if (
        verifyDniNie(
          user,
          tenantExtraField?.payslip_data?.payslip1?.data?.nieDniNumber
        )
      ) {
        payslip_data = tenantExtraField?.payslip_data?.payslip1?.data;
        source = "payslip1";
      } else if (
        verifyDniNie(
          user,
          tenantExtraField?.payslip_data?.payslip2?.data?.nieDniNumber
        )
      ) {
        payslip_data = tenantExtraField?.payslip_data?.payslip2?.data;
        source = "payslip2";
      } else if (
        verifyDniNie(
          user,
          tenantExtraField?.payslip_data?.payslip3?.data?.nieDniNumber
        )
      ) {
        payslip_data = tenantExtraField?.payslip_data?.payslip3?.data;
        source = "payslip3";
      }

      //Verify DNI/NIE with user and payslip else check in other method
      if (haveValue(payslip_data)) {
        return {
          amount: Math.round(payslip_data?.netSalary),
          method: _t("payslip"),
          source,
        };
      }
    }

    // declared income
    if (haveValue(user?.current_annual_income)) {
      return {
        amount: Math.round((user?.current_annual_income ?? 0) / 12),
        method: _t("declaredIncome"),
        source: "declaredIncome",
      };
    }
    return {
      amount: 0,
      method: _t("noIncomeSource"),
      source: "noIncomeSource",
    };
  }
};

const hierarchyBasedAmount = (user, landlordUser, _t) => {
  let tenantExtraField = user?.tenant_extra_field_id;
  for (let type of landlordUser?.agency_id?.income_hierarchy) {
    if (type == "algoanIncome") {
      if (haveValue(user?.algoanIncome?.monthlyAmount)) {
        return {
          amount: Math.round(user?.algoanIncome?.monthlyAmount),
          method: _t("bank"),
          source: "bank",
        };
      }
    } else {
      if (haveValue(user?.current_annual_income)) {
        return {
          amount: Math.round((user?.current_annual_income ?? 0) / 12),
          method: _t("declaredIncome"),
          source: "declaredIncome",
        };
      } else {
        return {
          amount: 0,
          method: _t("noIncomeSource"),
          source: "noIncomeSource",
        };
      }
    }
  }
};

export function isObject(value) {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

export function getDocumentsFromEmploymentType(user, needProtectedHousing) {
  let allDocs = {};
  const employment = user?.employment_status;

  if (employment === constants.employed) {
    allDocs =
      user?.employment_category === constants.domestic_worker
        ? docsData.DOMESTIC_WORKER_DOCS
        : docsData.EMPLOYED_DOCS;
  } else if (employment === constants.self_employed) {
    allDocs =
      user.employment_category === "freelancer"
        ? docsData.FREELANCER_DOCS
        : docsData.SELF_EMPLOYED_DOCS;
  } else if (employment === constants.student) {
    allDocs = docsData.STUDENT_DOCS;
  } else if (employment === constants.retired) {
    allDocs = docsData.RETIRED_DOCS;
  } else if (employment === constants.unemployed) {
    allDocs = docsData.UNEMPLOYED_DOCS;
  } else if (employment === constants.public_servant) {
    allDocs = docsData.PUBLIC_SERVANT_DOCS;
    if (user?.employment_category === "military") {
      allDocs = { ...allDocs, ...docsData.MILITARY_DOCS };
    }
  }
  if (user?.protected_housing && needProtectedHousing) {
    allDocs = { ...allDocs, ...docsData.PROTECTED_HOUSING };
  }

  return allDocs;
}
export const checkSalary = (user = {}) => {
  if (user?.algoanIncome) {
    let monthlyIncome = parseInt(user?.algoanIncome?.monthlyAmount);

    let userIncome = parseInt(
      haveValue(user.current_annual_income) ? user.current_annual_income : 0
    );
    let salaryMatch = Math.round((monthlyIncome / userIncome) * 100);

    return {
      matched: salaryMatch,
      salary: monthlyIncome,
    };
  }
};

export const isDefaulter = (experianData) => {
  if (
    !experianData ||
    !experianData.ResumenCais ||
    typeof experianData.ResumenCais !== "object"
  ) {
    return false;
  }

  const keysToCheck = [
    "NumeroOperacionesImpagadas",
    "NumeroCuotasImpagadas",
    "ImporteImpagado",
    "MaximoImporteImpagado",
  ];

  for (let key of keysToCheck) {
    const value = parseInt(experianData.ResumenCais[key]);
    if (!isNaN(value) && value > 0) {
      return true;
    }
  }

  return false;
};

export const amlKycChecks = (user) => {
  const kycCheck =
    (user?.onfido_user_kyc?.onfido_workflow_res?.object?.status ??
      user?.onfido_user_kyc?.onfido_workflow_res?.status) === "approved";
  if (kycCheck) {
    return true;
  } else {
    return false;
  }
};

export const formatToStandardDate = (dateInput) => {
  if (!dateInput) return dateInput;

  // Normalize input to string and trim
  const dateStr = typeof dateInput === "string" ? dateInput.trim() : dateInput;

  // Try parsing common formats first
  let m = moment(
    dateStr,
    ["DD/MM/YYYY", "MM/YYYY", "YYYY-MM-DD", "MMM YYYY", "MMMM YYYY"],
    true
  );

  // Fallback to native Date if moment fails
  if (!m.isValid()) {
    m = moment(new Date(dateStr));
  }

  // Final check
  return m.isValid() ? m.format("MMM YYYY") : dateInput;
};

export const getIdentityDetails = (user, userKycInfo, needOnfidoResult) => {
  const kyc = userKycInfo?.onfido_doc_data;
  if (
    haveValue(kyc?.personal_number) &&
    kyc?.document_issuing_country === "ESP" &&
    kyc?.document_type !== "passport"
  ) {
    return {
      type: "dni", // nie is also comes under this
      value: kyc.personal_number,
      showTick: true,
      name: kyc?.first_name + " " + (kyc?.last_name ?? ""),
    };
  }

  if (haveValue(kyc?.document_number) && kyc?.document_type === "passport") {
    return {
      type: "passport",
      value: kyc.document_number,
      showTick: true,
      name: kyc?.first_name + " " + (kyc?.last_name ?? ""),
    };
  }

  if (
    (needOnfidoResult && haveValue(kyc?.document_number)) ||
    haveValue(kyc?.personal_number)
  ) {
    return {
      type: kyc?.document_type === "passport" ? "passport" : "dni",
      value: kyc?.personal_number ?? kyc?.document_number,
      showTick: true,
      name: kyc?.first_name + " " + (kyc?.last_name ?? ""),
    };
  }

  if (haveValue(user?.dni_nie)) {
    return {
      type: "dni",
      value: user.dni_nie,
      showTick: false,
    };
  }

  return {
    type: "passport",
    value: user?.passport_id ?? "",
    showTick: false,
  };
};

export function detectIdentityType(identityNumber) {
  if (!identityNumber || typeof identityNumber !== "string") return "unknown";

  const id = identityNumber.toUpperCase().trim();

  const isDNI = /^[0-9]{8,9}[A-Z]$/;
  const isNIE = /^[XYZ][0-9]{7}[A-Z]$/;

  if (isDNI.test(id)) {
    return "dni";
  } else if (isNIE.test(id)) {
    return "nie";
  }

  return "passport";
}

export const LANDLORD_PATHS = [
  "/profile-request",
  "/profile-property",
  "/landlord/add-property",
];

export const validateDniNie = (id, type) => {
  if (!id) return false;

  const dniLetters = "TRWAGMYFPDXBNJZSQVHLCKE";

  let letterPart = id.slice(-1).toUpperCase();

  if (type === "dni") {
    const number = parseInt(id.slice(0, -1), 10);
    if (isNaN(number)) return false;
    const expectedLetter = dniLetters[number % 23];
    return expectedLetter === letterPart;
  }

  if (type === "nie") {
    // NIE: Starts with X, Y, or Z + 7 digits + letter
    const prefix = id.charAt(0).toUpperCase();
    let replacedPrefix = "";

    if (prefix === "X") replacedPrefix = "0";
    else if (prefix === "Y") replacedPrefix = "1";
    else if (prefix === "Z") replacedPrefix = "2";
    else return false;

    const numeric = replacedPrefix + id.slice(1, -1);
    const number = parseInt(numeric, 10);
    if (isNaN(number)) return false;

    const expectedLetter = dniLetters[number % 23];
    return expectedLetter === letterPart;
  }

  return false;
};

export const removeLeadingZerosAndHyphen = (num) =>
  num?.replace(/^0+/, "").replace(/-/g, "") || "";

export const cleanIdentityNumber = (value) => {
  if (!haveValue(value)) return "";

  return value
    .toString()
    .replace(/[^a-zA-Z0-9]/g, "")
    .toUpperCase();
};

export const streetTypes = [
  "No aplicable",
  "Acceso",
  "Acequia",
  "Acera",
  "Alameda",
  "Aldea",
  "Alquería",
  "Alto",
  "Andador",
  "Angosta",
  "Apartamentos",
  "Arboleda",
  "Arrabal",
  "Arroyo",
  "Atajo",
  "Autopista",
  "Autovía",
  "Avenida",
  "Bajada",
  "Barranco",
  "Barreduela",
  "Barriada",
  "Barrio",
  "Belena",
  "Bloque",
  "Brazal",
  "Bulevar",
  "Calle",
  "Calleja",
  "Callejón",
  "Callejuela",
  "Callizo",
  "Calzada",
  "Camino",
  "Campa",
  "Campo",
  "Canal",
  "Cañada",
  "Cantón",
  "Carrera",
  "Carretera",
  "Carretil",
  "Carril",
  "Caserío",
  "Cerro",
  "Cinturón",
  "Cobertizo",
  "Colonia",
  "Complejo",
  "Conjunto",
  "Cordel",
  "Corralillo",
  "Corredera",
  "Corredor",
  "Corrillo",
  "Cortijo",
  "Costa",
  "Costanilla",
  "Cuesta",
  "Entrada",
  "Escalera",
  "Escalinata",
  "Espalda",
  "Estrada",
  "Explanada",
  "Extramuros",
  "Extrarradio",
  "Fábrica",
  "Falda",
  "Finca",
  "Galeria",
  "Glorieta",
  "Gran vía",
  "Granja",
  "Grupo",
  "Hoya",
  "Jardín",
  "Ladera",
  "Lago",
  "Loma",
  "Lugar",
  "Malecón",
  "Masía",
  "Mazo",
  "Mirador",
  "Monte",
  "Muelle",
  "Pago",
  "Paraje",
  "Parque",
  "Particular",
  "Partida",
  "Pasadizo",
  "Pasaje",
  "Paseo",
  "Pasillo",
  "Pinar",
  "Pista",
  "Placeta",
  "Playa",
  "Plaza",
  "Plazoleta",
  "Plazuela",
  "Poblado",
  "Polígono",
  "Portal",
  "Portillo",
  "Prolongación",
  "Puente",
  "Ramal",
  "Rambla",
  "Rambleta",
  "Rampa",
  "Replaceta",
  "Ribera",
  "Rincón",
  "Rinconada",
  "Ronda",
  "Rotonda",
  "Rúa",
  "Salida",
  "Sector",
  "Senda",
  "Sendero",
  "Serventía",
  "Subida",
  "Torre",
  "Tránsito",
  "Transversal",
  "Trasera",
  "Travesía",
  "Túnel",
  "Urbanización",
  "Valle",
  "Vega",
  "Venera",
  "Vereda",
  "Vía",
  "Viaducto",
  "Vial",
  "Zona",
];

export const calculatePaymentScore = async (
  userToken,
  accountverificationid,
  transactionreportid
) => {
  let url = `${process.env.REACT_APP_SERVER_URL}/remote/calculatescore?userToken=${userToken}&accountverificationid=${accountverificationid}&transactionreportid=${transactionreportid}`;

  axiosInstance
    .get(url)
    .then((res) => {
      const url = new URL(window.location);
      url.searchParams.delete("account_verification_report_id");
      url.searchParams.delete("transaction_report_id");
      url.searchParams.delete("userToken");
      window.history.replaceState(
        {},
        document.title,
        url.pathname + url.search
      );
    })
    .catch((err) => {
      let err_res = err;
      console.log(err_res);
    });
};

export const clearPaymentData = async (userToken) => {
  return new Promise((resolve) => {
    let url = `${process.env.REACT_APP_SERVER_URL}/remote/algoan-info?userToken=${userToken}`;

    axiosInstance
      .delete(url)
      .then((res) => {
        return resolve(true);
      })
      .catch((err) => {
        let err_res = err;
        console.log(err_res.message);
        return resolve(null);
      });
  });
};

export const getUpdatedExchangeIncome = (
  userIncome,
  currency,
  originalUser
) => {
  const exchangeRate =
    originalUser?.common?.currency_exchange?.rates?.[currency] ?? 1;

  return Math.round(userIncome * exchangeRate);
};

const STATUS_STYLES = {
  yellow: {
    backgroundColor: "#FFFBEC",
    color: "#E1BB20",
    color2: "#EBD686",
    border: "1px solid #F2E09B",
  },
  blue: {
    backgroundColor: "#F1F6FF",
    color: "#074395",
    color2: "#6487BB",
    border: "1px solid #D7E4F6",
  },
  grey: {
    backgroundColor: "#F7F7F7",
    color: "#253837",
    color2: "#7F7F7F",
    border: "1px solid #E6E6E6",
  },
  green: {
    backgroundColor: "#F2FBE9",
    color: "#82CF3F",
    border: "1px solid #DAECC8",
  },
  red: {
    backgroundColor: "#FFE2E2",
    color: "#FF3C3C",
    color2: "#EB9B9C",
    border: "1px solid #EB9B9C",
  },
};

export const getGuarantorStatus = (status, data) => {
  switch (status) {
    case "pending":
      if (data?.step <= 3) {
        return {
          status: "pendingDocUpload",
          pendingFrom: "byYou",
          style: STATUS_STYLES.yellow,
        };
      }
      return {
        status: "pendingKyc",
        pendingFrom: "byYou",
        style: STATUS_STYLES.yellow,
      };

    case "processing":
    case "present":
      return {
        status: "pendingAcceptance",
        pendingFrom: "byGuarantor",
        style: STATUS_STYLES.yellow,
      };

    case "accepted":
      if (data?.step <= 3 && data?.profile_completed === false) {
        return {
          status: "pendingDocUpload",
          pendingFrom: "byGuarantor",
          style: STATUS_STYLES.yellow,
        };
      } else if (data?.step === 4 && data?.profile_completed === false) {
        return {
          status: "pendingKyc",
          pendingFrom: "byGuarantor",
          style: STATUS_STYLES.yellow,
        };
      } else if (data?.isForensicExists === false) {
        return {
          status: "analysisRunning",
          pendingFrom: "bySkor",
          style: STATUS_STYLES.yellow,
        };
      }
      return {
        status: "completed",
        style: STATUS_STYLES.green,
      };

    case "rejected":
      return {
        status: "declined",
        pendingFrom: "byGuarantor",
        style: STATUS_STYLES.red,
      };

    case "pendingDetails":
      return {
        status: "pendingDetails",
        pendingFrom: "byYou",
        style: STATUS_STYLES.yellow,
      };

    default:
      return {
        status: "pending",
        style: STATUS_STYLES.yellow,
      };
  }
};

export const removeUuidFromFileName = (fileName) => {
  if (!fileName) return "";

  const dotIndex = fileName.lastIndexOf(".");
  const ext = fileName.slice(dotIndex); // ".pdf"
  const namePart = fileName.slice(0, dotIndex);

  // Remove everything after "__"
  const cleanName = namePart.split("__")[0];

  return `${cleanName}${ext}`;
};

/**
 * Determine if 5 credits will be deducted on next profile view.
 * Works for both SharedProfile and AgencyTenantLink flows.
 */
export async function shouldDeductNextView(
  user,
  sharedProfileId,
  tenantId = null,
  workspaceUuid = null
) {
  try {
    if (workspaceUuid || user?.current_workspace?.self_serving) {
      const url = new URL(
        `${process.env.REACT_APP_SERVER_URL}/landlords/shouldDeductNextProfileView`
      );
      if (tenantId) {
        url.searchParams.append("tenantId", tenantId);
      }
      const response = await axiosInstance.post(
        url.toString(),
        { sharedProfileId },
        {
          headers: {
            "Accept-Language": localStorage.getItem("i18nextLng"),
            ...(workspaceUuid && { "x-workspace-id": workspaceUuid }),
          },
        }
      );

      return response?.data?.shouldDeduct ?? true;
    } else {
      return false;
    }
  } catch (err) {
    console.log(err, "Error in shouldDeductNextView");
    return true;
  }
}
export const isValidWorkspaceId = (id) =>
  typeof id === "string" && /^[0-9A-Za-z]{6}$/.test(id);

/**
 * Check if an email address is Domain or Institutional
 */

export const getInstitutionalDomainName = (email) => {
  if (!email || !email.includes("@")) return null;

  const domain = email.split("@")[1].toLowerCase();

  if (freeEmailDomains.includes(domain)) {
    return null;
  }

  return domain.split(".").slice(-2, -1)[0];
};

export const toast = {
  success: (msg) =>
    mainToast(<CustomToast type="success" title={msg} />, {
      className: "toast-wrapper",
      closeButton: false,
      hideProgressBar: true,
      position: "bottom-right",
    }),
  error: (msg) =>
    mainToast(<CustomToast type="error" title={msg} />, {
      className: "toast-wrapper",
      closeButton: false,
      hideProgressBar: true,
      position: "bottom-right",
    }),
};

export function resolveIncomeSelection(incomeSelection, tenantId) {
  if (!incomeSelection) return null;
  if (
    incomeSelection.tenant_source !== undefined &&
    incomeSelection.tenant_source?.length > 0
  )
    return incomeSelection;
  if (tenantId) return incomeSelection[tenantId] || null;
  return null;
}

export const placeNotificationLink = (notification, workspaceId) => {
  if (notification?.shared_profile_id?.pendingDecision === true) {
    return;
  }
  if (notification.notification_type == "guarantor_removed")
    return `/${workspaceId}/profile-property?sharedprofileid=${notification?.shared_profile_id?._id}`;
  if (notification.body.includes("linked your profile to a address."))
    return `/profile-shared?filter=${notification?.landlord_fname} ${notification?.landlord_lname}`;
  if (notification.notification_type == "property_assigned") {
    if (
      !haveValue(notification?.received_by_tenant?.assigned_property_id) &&
      notification?.assigned_property_id?.status == constants.TENANT_PENDING
    ) {
      return `/accept/assigned/property/${notification?.assigned_property_id?._id}`;
    } else if (
      haveValue(notification?.received_by_tenant?.assigned_property_id) &&
      notification?.assigned_property_id?._id ==
        notification?.received_by_tenant?.assigned_property_id
    ) {
      return `/awarded/property/${notification?.received_by_tenant?.assigned_property_id}`;
    } else if (
      notification?.assigned_property_id?.status != constants.TENANT_PENDING
    ) {
      return `${
        haveValue(workspaceId) ? `/${workspaceId}` : ``
      }/notification#no_longer_available`;
    } else {
      return `${
        haveValue(workspaceId) ? `/${workspaceId}` : ``
      }/notification#already_awarded`;
    }
  }
  if (notification.notification_type == "profile_sharing") {
    return `/${workspaceId}/profile-property?sharedprofileid=${notification?.shared_profile_id?._id}`;
  }
  if (notification.notification_type == "profile_sharing_request") {
    return `/workspace-dashboard?new_application=true`;
  }
  if (
    notification.notification_type == "document_updated" ||
    notification.notification_type == "financial_info_updated" ||
    notification.notification_type == "financial_info_updated_guarantor" ||
    notification.notification_type == "guarantor_added" ||
    notification.notification_type == "guarantor_document_updated"
  ) {
    // user?.isParentLL ? "agencylink" : "sharedprofileid"
    return `/${workspaceId}/profile-property?${"sharedprofileid"}=${
      // notification?.agency_profile_id?._id ??
      notification?.shared_profile_id?._id
    }`;
  }
  if (notification.notification_type == "profile_sharing_accepted") {
    if (notification?.shared_profile_id?.application_id?._id) {
      const title = notification?.shared_profile_id?.application_id?.title;
      if (haveValue(title)) {
        return `/${workspaceId}/profile-request?search=${encodeURIComponent(title)}`;
      }
      if (notification?.shared_profile_id?._id) {
        return `/${workspaceId}/profile-request?sharedprofileid=${notification?.shared_profile_id?._id}`;
      }
    }
  }
};

export const isDefaultWorkspace = (userData) => {
  return (
    userData?.current_workspace?._id?.toString() ===
    userData?.workspaceIds?.[0]?._id?.toString()
  );
};

// if the user is owner of the workspace
export const isOwnerOfWorkspace = (userData, workspaceData) => {
  return (
    userData?.id?.toString() === workspaceData?.landlordId?._id?.toString() &&
    workspaceData?.role?.toLocaleLowerCase() === "owner"
  );
};

function incomeVariation(user, originalUser, isDeaWorkspace, employmentType) {
  const incomes = [];
  //FOR DECLARED INCOME
  const declaredIncome = getUpdatedExchangeIncome(
    user.current_annual_income,
    user.currency,
    originalUser
  );
  if (haveValue(declaredIncome)) {
    incomes.push(declaredIncome);
  }

  //FOR ITR INCOME
  let tempItrAmount;
  let tempItrCurrency;

  const isTargetEmployment =
    employmentType === "Employee" ||
    employmentType === "Self-employed (Company Owner)";

  if (
    originalUser?.current_workspace?.dea_rules_activated &&
    isDeaWorkspace &&
    isTargetEmployment
  ) {
    tempItrAmount =
      user?.tenant_extra_field_id?.forensic_financial_data
        ?.itrIncomeAfterDeduction?.amount || 0;
    tempItrCurrency =
      user?.tenant_extra_field_id?.forensic_financial_data
        ?.itrIncomeAfterDeduction?.currency ||
      user?.tenant_extra_field_id?.forensic_financial_data?.itr?.currency;
  } else {
    tempItrAmount =
      user?.tenant_extra_field_id?.forensic_financial_data?.itr?.amount;
    tempItrCurrency =
      user?.tenant_extra_field_id?.forensic_financial_data?.itr?.currency;
  }

  if (haveValue(tempItrAmount) && tempItrAmount > 0) {
    const tempVal =
      getUpdatedExchangeIncome(tempItrAmount, tempItrCurrency, originalUser) /
      12;
    incomes.push(tempVal);
  }

  //FOR PAYLIPS
  if (haveValue(user?.tenant_extra_field_id?.forensic_financial_data)) {
    const payslipArray = Object?.entries(
      user?.tenant_extra_field_id?.forensic_financial_data
    )?.filter(([k, v]) => k?.startsWith("payslip:"));
    if (haveValue(payslipArray) && payslipArray?.length > 0) {
      payslipArray.map((value) => {
        incomes.push(
          getUpdatedExchangeIncome(
            value[1].amount,
            value[1].currency,
            originalUser
          )
        );
      });
    }
  }
  //FOR BANK EXTRACTED VALUE
  if (haveValue(user?.algoanIncome?.monthlyAmount)) {
    incomes.push(user?.algoanIncome?.monthlyAmount);
  }

  if (!Array.isArray(incomes) || incomes.length < 2) {
    console.log("Provide at least 2 income values.");
  }

  const mean = incomes.reduce((a, b) => a + b, 0) / incomes.length;

  if (mean === 0) return 0;

  const variance =
    incomes.map((x) => Math.pow(x - mean, 2)).reduce((a, b) => a + b, 0) /
    incomes.length;

  const stdDev = Math.sqrt(variance);

  const coefficientOfVariation = stdDev / mean;

  const variationScore = Math.min(100, coefficientOfVariation * 100);

  return Number(variationScore.toFixed(2));
}

const incomeVerificationPolicy = (user, originalUser) => {
  try {
    const results = [];

    const incomeData = user?.tenant_extra_field_id?.forensic_financial_data;

    /* ---------- 1️⃣ ITR ---------- */
    if (incomeData?.itr?.amount) {
      const type =
        incomeData.itr?.origin == "Local"
          ? "Tax returns"
          : "Foreign payslip/Tax Report";
      results.push({
        source: type,
        amount: incomeData.itr.amount,
        verified: !!incomeData.itr.verified,
      });
    }

    /* ---------- 2️⃣ PAYSLIPS ---------- */
    const payslips = Object.entries(incomeData || {})
      .filter(([key]) => key.startsWith("payslip:"))
      .map(([_, value]) => value);

    // Sort payslips by amount descending (optional but logical)
    payslips.sort((a, b) => b.amount - a.amount);

    for (const slip of payslips) {
      if (results.length >= 2) break;

      const type =
        slip.origin === "Local"
          ? "Local payslip"
          : "Foreign payslip/Tax Report";

      results.push({
        source: type,
        amount: slip.amount,
        verified: !!slip.verified,
      });
    }

    /* ---------- 3️⃣ BANK VALUE ---------- */
    if (results.length < 2) {
      const bankAmount = user?.algoanIncome?.monthlyAmount;

      if (bankAmount) {
        const provider =
          user?.bank_details?.userDataByProvider?.[0]?.providerName;

        const verified = provider && provider !== "Uploaded from CSV";

        results.push({
          source: "Bank Connection",
          amount: bankAmount,
          verified,
        });
      }
    }

    /* ---------- 4️⃣ SELF DECLARED ---------- */
    if (results.length < 2) {
      const annualIncome = user?.current_annual_income;

      if (annualIncome) {
        results.push({
          source: "Self-declared",
          amount: annualIncome,
          verified: false,
        });
      }
    }

    return results.slice(0, 2);
  } catch (err) {
    console.log(err);
  }
};

const getBankBehaviour = () => {
  return null;
};

const getEmploymentDocsMissing = async (user) => {
  try {
    if (haveValue(user?._id)) {
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_SERVER_URL}/tenants/get/missing/docs/${user?._id}`,
        {
          headers: {
            "Accept-Language": localStorage.getItem("i18nextLng"),
          },
        }
      );
      return response?.data?.success_res;
    }
  } catch (err) {
    console.log(err);
    throw err;
  }
};

const getForensicResultForPolicy = () => {
  return null;
};

const getRiskMitigation = (user, giveInsurance = false) => {
  try {
    const experianData =
      user?.tenant_extra_field_id?.experianJson?.["s:Envelope"]?.["s:Body"]
        ?.GenerarInformeResponse?.GenerarInformeResult?.InformeDelphi4A3;

    const guarantorCounts = user?.gurantor_id?.length;
    if (giveInsurance) {
      return "Insured by Skor";
    } else if (guarantorCounts >= 2) {
      return "2 Guarantors";
    } else if (guarantorCounts == 1) {
      return "1 Guarantor";
    } else if (!isDefaulter(experianData)) {
      return "Eligible but not taken";
    } else {
      const error = { label: "riskHardFail" };
      error.isHardFail = true;
      error.status = 400;
      return error;
    }
  } catch (err) {
    throw err;
  }
};

const getDefaultHistory = (user) => {
  let defaulterStatus = isDefaulter(
    user?.tenant_extra_field_id?.experianJson?.["s:Envelope"]?.["s:Body"]
      ?.GenerarInformeResponse?.GenerarInformeResult?.InformeDelphi4A3
  );
  let useCase = 1;

  try {
    if (!defaulterStatus) {
      return "No defaults ever";
    } else if (useCase == 2) {
      return "Defaults >3 years ago (resolved)";
    } else if (useCase == 3) {
      return "Defaults <3 years ago (resolved)";
    } else {
      const error = { label: "defaulterHardFail" };
      error.isHardFail = true;
      error.status = 400;
      return error;
    }
  } catch (err) {
    throw err;
  }
};

export const getFieldForPolicies = async (
  user,
  originalUser,
  finalEffortRate,
  giveInsurance = false,
  isDeaWorkspace = false,
  missingDocsResult = null
) => {
  try {
    //Check hardfail for KYC/AML
    let kycAmlFailCheck = {};
    if (!user?.onfido_kyc_success) {
      const error = { label: "KYCHardFail" };
      error.isHardFail = true;
      error.status = 400;
      kycAmlFailCheck = error;
    }
    //Check hardfail for KYC/AML end

    const kycDetails = getIdentityDetails(user, user?.onfido_user_kyc);
    const allReports =
      user?.tenant_extra_field_id?.forensic_analysis?.profileReports ?? [];
    let matchedReport;
    if (haveValue(kycDetails.value)) {
      matchedReport =
        allReports.find((report) =>
          report?.report?.keyFactors?.keyFactors?.some((kf) => {
            if (!kf?.dataInJSON || typeof kycDetails?.value !== "string")
              return false;

            let expectedKey;
            if (kycDetails.type?.toLowerCase() === "dni") {
              expectedKey = "dni";
            } else if (kycDetails.type?.toLowerCase() === "passport") {
              expectedKey = "passportNumber";
            } else {
              return false; // type not supported
            }

            const fieldValue = kf?.dataInJSON?.[expectedKey];
            return (
              typeof fieldValue === "string" &&
              fieldValue.toLowerCase() === kycDetails.value.toLowerCase()
            );
          })
        ) || allReports[0];
    } else {
      matchedReport = allReports[0];
    }
    const temp_data = matchedReport?.report;
    const temp_key = {};
    temp_data?.keyFactors?.keyFactors.map((val) => {
      temp_key[val.keyFactor] = val;
    });
    const doc_list = {};
    (temp_data?.documents_list ?? []).map((val) => {
      doc_list[val?.document_id] = val;
    });

    const currentEmploymentHistory =
      temp_key?.["Employment History"]?.dataInJSON?.employmentHistory?.find(
        (item) => item?.isCurrentEmployer
      ) || temp_key?.["Employment History"]?.dataInJSON?.employmentHistory?.[0];

    let SECTOR_CYCLICALITY = currentEmploymentHistory?.industry?.id;

    let jobStartDate = currentEmploymentHistory?.jobStartedOn
      ? moment(currentEmploymentHistory?.jobStartedOn, "YYYY-MM-DD")
      : moment();
    const jobEndDate = currentEmploymentHistory?.jobEndOn
      ? moment(currentEmploymentHistory?.jobEndOn, "YYYY-MM-DD")
      : moment();

    let TENURE = jobEndDate.diff(jobStartDate, "months");
    let EMPLOYMENT_PREDICTABILITY =
      currentEmploymentHistory?.employmentPredictability?.id;
    let EMPLOYER_STABILITY = currentEmploymentHistory?.employerStability?.id;

    let employmentType =
      temp_key?.["Employment Type"]?.dataInJSON?.employmentType;
    let INCOME_CONSISTENCY = incomeVariation(
      user,
      originalUser,
      isDeaWorkspace,
      employmentType
    ); // Done
    let INCOME_VERIFICATION = incomeVerificationPolicy(user, originalUser);
    // let EFFORT_RATE_SCORE = effortRateForPolicy(user, originalUser, rentVal); // Done
    let EFFORT_RATE_SCORE = {
      EFFORT_RATE_SCORE: finalEffortRate,
    }; // Done

    if (DEBUG_CALCULATION) {
      console.log(
        "[getFieldForPolicies] finalEffortRate =",
        finalEffortRate,
        "type:",
        typeof finalEffortRate,
        ">50:",
        finalEffortRate > 50
      );
    }
    if (finalEffortRate > 50) {
      if (DEBUG_CALCULATION) {
        console.log(
          "[getFieldForPolicies] *** EFFORT RATE HARD FAIL TRIGGERED ***"
        );
      }
      const error = { label: "EffortRateHardFail" };
      error.isHardFail = true;
      error.status = 400;
      EFFORT_RATE_SCORE = { EFFORT_RATE_SCORE: finalEffortRate, ...error };
    }

    let FINANCIAL_SKOR = user?.paymentScores?.creditScore?.value; // Done
    let BANK_BEHAVIOUR = getBankBehaviour(user);
    let RENT_PAYMENT_BEHAVIOUR =
      user?.tenant_extra_field_id?.tink_report_data?.data?.housingBifercation?.[
        "Rent Payment"
      ]?.length ?? 0;
    let DEFAULT_HISTORY = getDefaultHistory(user);
    let RISK_MITIGATION_SUB_SCORE = getRiskMitigation(user, giveInsurance);
    let DOCUMENTATION_RES =
      missingDocsResult ??
      user?._snapshotMissingDocs ??
      (await getEmploymentDocsMissing(user));
    let DOCUMENTATION = DOCUMENTATION_RES?.missingCount ?? 0;
    let MISSING_DOCS = DOCUMENTATION_RES?.missingDocs ?? [];
    let FORENSICS = getForensicResultForPolicy(user);
    return {
      kycAmlFailCheck,
      SECTOR_CYCLICALITY,
      TENURE,
      EMPLOYMENT_PREDICTABILITY,
      EMPLOYER_STABILITY,
      INCOME_CONSISTENCY,
      INCOME_VERIFICATION,
      EFFORT_RATE_SCORE,
      FINANCIAL_SKOR,
      BANK_BEHAVIOUR,
      RENT_PAYMENT_BEHAVIOUR,
      DEFAULT_HISTORY,
      RISK_MITIGATION_SUB_SCORE,
      DOCUMENTATION,
      FORENSICS,
      MISSING_DOCS,
    };
  } catch (err) {
    throw err;
  }
};

function clampScore(value) {
  return Math.max(0, Math.min(100, value));
}

export async function calculateFinalScore(
  scoringConfig,
  overlayConfig,
  policyConfig,
  applicantInput
) {
  const subScoreResults = {};
  const pillarResults = {};

  for (const sub of scoringConfig.subScores) {
    const key = sub.subScoreKey;
    let score = 0;

    // 1️⃣ Frozen value
    if (sub.frozenValue !== undefined) {
      score = sub.frozenValue;
    }

    // 2️⃣ Income Verification (Composite)
    else if (key === "INCOME_VERIFICATION") {
      score = calculateIncomeVerification(sub, applicantInput[key]);
    }

    // 3️⃣ Normal sub-score
    else {
      score = resolveSubScore(sub, applicantInput[key]);
    }

    // 4️⃣ Apply overlay if exists
    const baseScore = score?.score ?? score;

    const adjustedScore = applyOverlay(
      sub,
      baseScore,
      applicantInput[key],
      overlayConfig
    );

    score = {
      score: adjustedScore,
      label: score?.label ?? "",
    };

    subScoreResults[key] = {
      score: clampScore(score?.score ?? score),
      label: score?.label ?? "",
    };
    console.log(score, "Here is score for", sub.subScoreKey);
  }

  // 5️⃣ Pillars
  for (const pillar of scoringConfig.pillars) {
    let total = 0;

    for (const map of pillar.mappings) {
      total += (subScoreResults[map.subScoreKey]?.score ?? 0) * map.weight;
    }

    pillarResults[pillar.pillarKey] = clampScore(total);
  }

  // 6️⃣ Final score
  let finalScore = 0;

  for (const weightObj of policyConfig.pillarWeights) {
    finalScore += pillarResults[weightObj.pillarKey] * weightObj.weight;
  }

  return {
    subScores: subScoreResults,
    pillars: pillarResults,
    finalScore: clampScore(Number(finalScore.toFixed(2))),
  };
}

function resolveSubScore(subConfig, value) {
  if (!subConfig.options) {
    return { score: 0, label: "No data" };
  }

  const hasNoDataOption = subConfig.options.find((o) => o.label === "No data");

  // ---------- HANDLE NULL / EMPTY ----------
  if (value === null || value === undefined || value === "") {
    if (hasNoDataOption) {
      return {
        score: hasNoDataOption.score,
        label: hasNoDataOption.label,
      };
    }
    return { score: 0, label: "No data" };
  }

  // ---------- NUMERIC RANGE ----------
  if (typeof value === "number") {
    const match = subConfig.options.find(
      (o) =>
        o.min !== undefined &&
        o.max !== undefined &&
        value >= o.min &&
        value < o.max
    );

    if (match) {
      return {
        score: match.score,
        label: match.label,
      };
    }

    if (hasNoDataOption) {
      return {
        score: hasNoDataOption.score,
        label: hasNoDataOption.label,
      };
    }

    return { score: 0, label: "No data" };
  }

  // ---------- MATCH BY label_id ----------
  const matchById = subConfig.options.find(
    (o) => o.label_id && o.label_id === value
  );

  if (matchById) {
    return {
      score: matchById.score,
      label: matchById.label,
    };
  }

  // ---------- MATCH BY label ----------
  const matchByLabel = subConfig.options.find((o) => o.label === value);

  if (matchByLabel) {
    return {
      score: matchByLabel.score,
      label: matchByLabel.label,
    };
  }

  // ---------- FALLBACK ----------
  if (hasNoDataOption) {
    return {
      score: hasNoDataOption.score,
      label: hasNoDataOption.label,
    };
  }

  return { score: 0, label: "No data" };
}

function calculateIncomeVerification(subConfig, incomeArray = []) {
  if (!incomeArray.length) return 0;

  const weights = subConfig.compositeRule.weight;

  const scores = incomeArray.slice(0, 2).map((entry) => {
    const option = subConfig.options.find((o) => o.label === entry.source);

    if (!option) return 0;

    let base = option.score;

    if (entry.verified && option.verified_bonus) {
      base += option.verified_bonus;
    }

    return { base, optionLabel: option.label };
  });

  const primary = scores[0]?.base || 0;
  const secondary = scores[1]?.base || 0;

  return {
    score: primary * weights[0] + secondary * weights[1],
    label: `${scores[0]?.optionLabel || ""}${scores[1] ? ` & ${scores[1].optionLabel}` : ""}`,
  };
}

function applyOverlay(subConfig, baseScore, inputValue, overlayConfig) {
  if (!overlayConfig) return baseScore;

  const overlays = Array.isArray(overlayConfig)
    ? overlayConfig
    : [overlayConfig];

  const overlay = overlays.find((o) => o.subScoreKey === subConfig.subScoreKey);

  if (!overlay) return baseScore;

  let matchedOption = null;

  /* ---------- FIND MATCHING OPTION FROM SCORING CONFIG ---------- */

  if (typeof inputValue === "number") {
    matchedOption = subConfig.options?.find(
      (o) =>
        o.min !== undefined &&
        o.max !== undefined &&
        inputValue >= o.min &&
        inputValue < o.max
    );
  } else {
    matchedOption =
      subConfig.options?.find((o) => o.label_id === inputValue) ||
      subConfig.options?.find((o) => o.label === inputValue);
  }

  if (!matchedOption) return baseScore;

  /* ---------- FIND OVERLAY RULE ---------- */

  const rule = overlay.categoryRules.find((r) => {
    // 1️⃣ Prefer ID match if exists
    if (r.optionLabelId && matchedOption.label_id) {
      return r.optionLabelId === matchedOption.label_id;
    }

    // 2️⃣ Fallback to label comparison
    return r.optionLabel === matchedOption.label;
  });

  if (!rule) return baseScore;

  /* ---------- APPLY CAPPED ADJUSTMENT ---------- */

  const adjustment = rule.landlordPoints * overlay.multiplier;

  const capped = Math.max(-overlay.cap, Math.min(overlay.cap, adjustment));

  return clampScore(baseScore + capped);
}

function getRangeMatch(ranges, value) {
  return ranges.find((item) => value >= item.min && value < item.max);
}

function getLabelMatch(options, values = []) {
  if (!Array.isArray(values)) {
    values = [values];
  }

  // Try to match any provided value
  for (const value of values) {
    const matched = options.find(
      (item) => item.labelId && item.labelId.includes(value)
    );

    if (matched) return matched;
  }

  // Fallback → object without labelId
  return options.find((item) => !item.labelId);
}

function getContractTypeByLabelId(contractOptions, labelId) {
  if (labelId === "NO_INCOME") {
    return contractOptions.find((item) => !item.labelId);
  }

  return contractOptions.find(
    (item) => item.labelId && item.labelId.includes(labelId)
  );
}

function getFirstLabelId(match) {
  return match?.labelId?.[0];
}

export function calculateOverlayScore(
  EFFORT_RATE_VALUE,
  TENURE_VALUE,
  EMPLOYMENT_PREDICTABILITY,
  SECTOR_CYCLICALITY,
  EMPLOYER_STABILITY
) {
  const effortMatch = getRangeMatch(
    DEA_OVERLAY.EFFORT_RATE,
    EFFORT_RATE_VALUE?.EFFORT_RATE_SCORE
  );

  const tenureMatch = getRangeMatch(DEA_OVERLAY.TENURE, TENURE_VALUE);

  const incomeTypeMatch = getLabelMatch(DEA_OVERLAY.INCOME_TYPE, [
    EMPLOYMENT_PREDICTABILITY,
    EMPLOYER_STABILITY,
  ]);

  let contractTypeMatch;

  const incomeLabelId = getFirstLabelId(incomeTypeMatch);

  if (incomeLabelId && DEA_OVERLAY.INCOME_TO_CONTRACT_MAP[incomeLabelId]) {
    const mappedContractLabelId =
      DEA_OVERLAY.INCOME_TO_CONTRACT_MAP[incomeLabelId];

    contractTypeMatch = getContractTypeByLabelId(
      DEA_OVERLAY.CONTRACT_TYPE,
      mappedContractLabelId
    );
  } else if (incomeTypeMatch?.label === "Unknown") {
    contractTypeMatch = DEA_OVERLAY.CONTRACT_TYPE.find((item) => !item.labelId);
  } else {
    contractTypeMatch = getLabelMatch(DEA_OVERLAY.CONTRACT_TYPE, [
      EMPLOYMENT_PREDICTABILITY,
      EMPLOYER_STABILITY,
    ]);
  }

  const riskSectorMatch = getLabelMatch(DEA_OVERLAY.RISK_SECTORS, [
    SECTOR_CYCLICALITY,
  ]);

  const finalScore =
    (effortMatch?.score || 0) +
    (tenureMatch?.score || 0) +
    (incomeTypeMatch?.score || 0) +
    (contractTypeMatch?.score || 0) +
    (riskSectorMatch?.score || 0);

  return {
    EFFORT_RATE: effortMatch,
    TENURE: tenureMatch,
    INCOME_TYPE: incomeTypeMatch,
    CONTRACT_TYPE: contractTypeMatch,
    RISK_SECTORS: riskSectorMatch,
    finalScore,
  };
}

export function haveMissingDniOrNieFromVerifiedSource(tenantsData = []) {
  console.log(tenantsData, "checktenantsData");
  return tenantsData.some((item) => {
    const kycDetails = getIdentityDetails(item, item?.onfido_user_kyc);
    const allReports =
      item?.tenant_extra_field_id?.forensic_analysis?.profileReports ?? [];
    let matchedReport;
    if (haveValue(kycDetails.value)) {
      matchedReport =
        allReports.find((report) =>
          report?.report?.keyFactors?.keyFactors?.some((kf) => {
            if (!kf?.dataInJSON || typeof kycDetails?.value !== "string")
              return false;

            let expectedKey;
            if (kycDetails.type?.toLowerCase() === "dni") {
              expectedKey = "dni";
            } else if (kycDetails.type?.toLowerCase() === "passport") {
              expectedKey = "passportNumber";
            } else {
              return false; // type not supported
            }

            const fieldValue = kf?.dataInJSON?.[expectedKey];
            return (
              typeof fieldValue === "string" &&
              fieldValue.toLowerCase() === kycDetails.value.toLowerCase()
            );
          })
        ) || allReports[0];
    } else {
      matchedReport = allReports[0];
    }
    const temp_data = matchedReport?.report;
    const temp_key = {};
    temp_data?.keyFactors?.keyFactors.map((val) => {
      temp_key[val.keyFactor] = val;
    });
    console.log(
      temp_key?.DNI?.dataInJSON?.dni,
      temp_key?.DNI?.verifiedSource,
      "checkthis"
    );
    const foundID = haveValue(temp_key?.DNI?.dataInJSON?.dni)
      ? temp_key?.DNI?.verifiedSource
      : haveValue(temp_key?.Passport?.dataInJSON?.passportNumber)
        ? temp_key?.Passport?.verifiedSource
        : false;
    return !foundID;
  });
}

export function tenantHasMissingDniNie(item) {
  const kycDetails = getIdentityDetails(item, item?.onfido_user_kyc);
  const allReports =
    item?.tenant_extra_field_id?.forensic_analysis?.profileReports ?? [];
  let matchedReport;
  if (haveValue(kycDetails.value)) {
    matchedReport =
      allReports.find((report) =>
        report?.report?.keyFactors?.keyFactors?.some((kf) => {
          if (!kf?.dataInJSON || typeof kycDetails?.value !== "string")
            return false;

          let expectedKey;
          if (kycDetails.type?.toLowerCase() === "dni") {
            expectedKey = "dni";
          } else if (kycDetails.type?.toLowerCase() === "passport") {
            expectedKey = "passportNumber";
          } else {
            return false;
          }

          const fieldValue = kf?.dataInJSON?.[expectedKey];
          return (
            typeof fieldValue === "string" &&
            fieldValue.toLowerCase() === kycDetails.value.toLowerCase()
          );
        })
      ) || allReports[0];
  } else {
    matchedReport = allReports[0];
  }
  const temp_data = matchedReport?.report;
  const temp_key = {};
  temp_data?.keyFactors?.keyFactors.map((val) => {
    temp_key[val.keyFactor] = val;
  });
  const foundID = haveValue(temp_key?.DNI?.dataInJSON?.dni)
    ? temp_key?.DNI?.verifiedSource
    : haveValue(temp_key?.Passport?.dataInJSON?.passportNumber)
      ? temp_key?.Passport?.verifiedSource
      : false;
  return !foundID;
}

export const getConsiderResult = (breakdown, path) => {
  const [section, field] = path.split(".");
  const sectionData = breakdown?.[section];
  if (!sectionData) return false;

  const fieldResult = sectionData?.breakdown?.[field]?.result;
  if (fieldResult === "consider") return true;

  const properties = sectionData?.breakdown?.[field]?.properties;
  if (properties) {
    return Object.values(properties).some((v) => v === "consider");
  }

  return false;
};

export const getOnfidoCustomMessage = (user, _t) => {
  const status = user?.onfido_user_kyc?.onfido_workflow_res?.resource?.status;
  const isDeclined = status === "declined";
  const isReview = status === "review";

  if (!haveValue(status) || (!isDeclined && !isReview)) return null;

  const breakdown = (
    user?.onfido_user_kyc?.onfido_workflow_res?.resource?.output ??
    user?.onfido_user_kyc?.onfido_workflow_res?.output
  )?.document_report?.breakdown;

  const documentType = (
    user?.onfido_user_kyc?.onfido_workflow_res?.resource?.output ??
    user?.onfido_user_kyc?.onfido_workflow_res?.output
  )?.document_type;

  if (!breakdown) return null;

  for (const {
    path,
    explanation,
    recommended_action,
  } of ONFIDO_TOOLTIP_PRIORITY) {
    if (getConsiderResult(breakdown, path)) {
      return {
        explanation: _t(explanation),
        recommended_action: _t(recommended_action),
        documentType,
      };
    }
  }

  return null;
};

export const ONFIDO_TOOLTIP_PRIORITY = [
  // ── PRIORITY 1 — Blockers ───────────────────────────────────────────────
  {
    path: "data_validation.document_expiration",
    message: "onfidoDataValidationDocumentExpirationMessage",
    explanation: "onfidoDataValidationDocumentExpirationExplanation",
    // "Your document has expired and cannot be accepted.",
    recommended_action:
      "onfidoDataValidationDocumentExpirationRecommendedAction",
    // "Please upload a valid, non-expired document."
  },
  {
    path: "image_integrity.supported_document",
    message: "onfidoImageIntegritySupportedDocumentMessage",
    explanation: "onfidoImageIntegritySupportedDocumentExplanation",
    // "The document type you uploaded is not accepted.",
    recommended_action:
      "onfidoImageIntegritySupportedDocumentRecommendedAction",
    // "Please upload a valid passport, national ID, or driving licence."
  },

  // ── PRIORITY 2 — Data issues ────────────────────────────────────────────
  {
    path: "data_validation.mrz",
    message: "onfidoDataValidationMrzMessage",
    explanation: "onfidoDataValidationMrzExplanation",
    // "The data strip on your document could not be read.",
    recommended_action: "onfidoDataValidationMrzRecommendedAction",
    // "Upload a clearer photo or use a different document."
  },
  {
    path: "data_validation.document_numbers",
    message: "onfidoDataValidationDocumentNumbersMessage",
    explanation: "onfidoDataValidationDocumentNumbersExplanation",
    // "We found a mismatch in your document number.",
    recommended_action: "onfidoDataValidationDocumentNumbersRecommendedAction",
    // "Make sure you're uploading the correct document and try again."
  },
  {
    path: "data_validation.date_of_birth",
    message: "onfidoDataValidationDateOfBirthMessage",
    explanation: "onfidoDataValidationDateOfBirthExplanation",
    // "There is an inconsistency with the date of birth on your document.",
    recommended_action: "onfidoDataValidationDateOfBirthRecommendedAction",
    // "Upload a clearer photo or use a different document."
  },
  {
    path: "data_validation.gender",
    message: "onfidoDataValidationGenderMessage",
    explanation: "onfidoDataValidationGenderExplanation",
    // "An inconsistency was detected in your document.",
    recommended_action: "onfidoDataValidationGenderRecommendedAction",
    // "Upload a clearer photo or use a different document."
  },

  // ── PRIORITY 3 — Visual/quality issues ─────────────────────────────────
  {
    path: "visual_authenticity.original_document_present",
    message: "onfidoVisualAuthenticityOriginalDocumentPresentMessage",
    explanation: "onfidoVisualAuthenticityOriginalDocumentPresentExplanation",
    // "It looks like you uploaded a photo of a photocopy instead of the original document.",
    recommended_action:
      "onfidoVisualAuthenticityOriginalDocumentPresentRecommendedAction",
    // "Take a photo of your original physical document."
  },
  {
    path: "visual_authenticity.picture_face_integrity",
    message: "onfidoVisualAuthenticityPictureFaceIntegrityMessage",
    explanation: "onfidoVisualAuthenticityPictureFaceIntegrityExplanation",
    // "The photo on your document could not be verified.",
    recommended_action:
      "onfidoVisualAuthenticityPictureFaceIntegrityRecommendedAction",
    // "Upload a clearer image where your face is fully visible."
  },
  {
    path: "visual_authenticity.fonts",
    message: "onfidoVisualAuthenticityFontsMessage",
    explanation: "onfidoVisualAuthenticityFontsExplanation",
    // "Your document could not be fully verified due to a quality issue.",
    recommended_action: "onfidoVisualAuthenticityFontsRecommendedAction",
    // "Upload a clearer photo or try a different document."
  },
  {
    path: "visual_authenticity.security_features",
    message: "onfidoVisualAuthenticitySecurityFeaturesMessage",
    explanation: "onfidoVisualAuthenticitySecurityFeaturesExplanation",
    // "The security features on your document could not be verified.",
    recommended_action:
      "onfidoVisualAuthenticitySecurityFeaturesRecommendedAction",
    // "Upload a higher-quality photo taken under good lighting."
  },
  {
    path: "image_integrity.image_quality",
    message: "onfidoImageIntegrityImageQualityMessage",
    explanation: "onfidoImageIntegrityImageQualityExplanation",
    // "Your document photo is too blurry or dark to be processed.",
    recommended_action: "onfidoImageIntegrityImageQualityRecommendedAction",
    // "Retake the photo in good lighting, keeping the document flat and still."
  },
  {
    path: "image_integrity.conclusive_document_quality",
    message: "onfidoImageIntegrityConclusiveDocumentQualityMessage",
    explanation: "onfidoImageIntegrityConclusiveDocumentQualityExplanation",
    // "Not enough detail could be extracted from your document photo.",
    recommended_action:
      "onfidoImageIntegrityConclusiveDocumentQualityRecommendedAction",
    // "Upload a clear, well-lit image of your full document."
  },

  // ── PRIORITY 4 — Generic fallback ──────────────────────────────────────
  {
    path: "visual_authenticity.other",
    message: "onfidoFallbackMessage",
    explanation: "onfidoFallbackExplanation",
    // "Your document could not be fully verified.",
    recommended_action: "onfidoFallbackRecommendedAction",
    // "Upload a clearer photo. If the issue persists, contact support."
  },
];

export const formatDateOfBirth = (dob, currentLang) => {
  if (!dob) return currentLang === "es" ? "Sin datos" : "No data";

  const formattedDate = moment(dob).format("DD/MM/YYYY");
  return moment(formattedDate, "DD/MM/YYYY").isValid()
    ? formattedDate
    : currentLang === "es"
      ? "Sin datos"
      : "No data";
};

const parseEmploymentDate = (dateInput) => {
  if (!haveValue(dateInput)) return null;

  const dateStr = typeof dateInput === "string" ? dateInput.trim() : dateInput;
  let parsedDate = moment(
    dateStr,
    ["YYYY-MM-DD", "DD/MM/YYYY", "MM/YYYY", "MMM YYYY", "MMMM YYYY"],
    true
  );

  if (!parsedDate.isValid()) {
    parsedDate = moment(new Date(dateStr));
  }

  return parsedDate.isValid() ? parsedDate.startOf("day") : null;
};

export const formatDurationBetweenDates = (
  startDate,
  endDate,
  fallback = "N/A",
  locale = "en-us"
) => {
  const parsedStartDate = parseEmploymentDate(startDate);
  const parsedEndDate = haveValue(endDate)
    ? parseEmploymentDate(endDate)
    : moment().startOf("day");

  if (
    !parsedStartDate ||
    !parsedEndDate ||
    parsedEndDate.isBefore(parsedStartDate)
  ) {
    return fallback;
  }

  const remainingDate = parsedStartDate.clone();
  const years = parsedEndDate.diff(remainingDate, "years");
  remainingDate.add(years, "years");
  const months = parsedEndDate.diff(remainingDate, "months");
  remainingDate.add(months, "months");
  const days = parsedEndDate.diff(remainingDate, "days");

  const isSpanish = locale?.toLowerCase?.().startsWith("es");
  const durationLabels = isSpanish
    ? {
        year: ["año", "años"],
        month: ["mes", "meses"],
        day: ["día", "días"],
      }
    : {
        year: ["year", "years"],
        month: ["month", "months"],
        day: ["day", "days"],
      };

  const durationParts = [
    { value: years, label: "year" },
    { value: months, label: "month" },
    { value: days, label: "day" },
  ]
    .filter(({ value }) => value > 0)
    .map(({ value, label }) => {
      const [singular, plural] = durationLabels[label];
      return `${value} ${value === 1 ? singular : plural}`;
    });

  return durationParts.length
    ? durationParts.join(" ")
    : `0 ${durationLabels.day[1]}`;
};

export const ISO3_TO_ISO2 = {
  AFG: "AF",
  AGO: "AO",
  AIA: "AI",
  ALB: "AL",
  AND: "AD",
  ARE: "AE",
  ARG: "AR",
  ARM: "AM",
  ATG: "AG",
  AUS: "AU",
  AUT: "AT",
  AZE: "AZ",
  BDI: "BI",
  BEL: "BE",
  BEN: "BJ",
  BFA: "BF",
  BGD: "BD",
  BGR: "BG",
  BHR: "BH",
  BHS: "BS",
  BIH: "BA",
  BLR: "BY",
  BLZ: "BZ",
  BMU: "BM",
  BOL: "BO",
  BRA: "BR",
  BRB: "BB",
  BRN: "BN",
  BTN: "BT",
  BWA: "BW",
  CAF: "CF",
  CAN: "CA",
  CHE: "CH",
  CHL: "CL",
  CHN: "CN",
  CIV: "CI",
  CMR: "CM",
  COD: "CD",
  COG: "CG",
  COL: "CO",
  COM: "KM",
  CPV: "CV",
  CRI: "CR",
  CUB: "CU",
  CYM: "KY",
  CYP: "CY",
  CZE: "CZ",
  DEU: "DE",
  DJI: "DJ",
  DMA: "DM",
  DNK: "DK",
  DOM: "DO",
  DZA: "DZ",
  ECU: "EC",
  EGY: "EG",
  ERI: "ER",
  ESP: "ES",
  EST: "EE",
  ETH: "ET",
  FIN: "FI",
  FJI: "FJ",
  FRA: "FR",
  FRO: "FO",
  FSM: "FM",
  GAB: "GA",
  GBR: "GB",
  GEO: "GE",
  GGY: "GG",
  GHA: "GH",
  GIB: "GI",
  GIN: "GN",
  GMB: "GM",
  GNB: "GW",
  GNQ: "GQ",
  GRC: "GR",
  GRD: "GD",
  GRL: "GL",
  GTM: "GT",
  GUY: "GY",
  HND: "HN",
  HRV: "HR",
  HTI: "HT",
  HUN: "HU",
  IDN: "ID",
  IMN: "IM",
  IND: "IN",
  IRL: "IE",
  IRN: "IR",
  IRQ: "IQ",
  ISL: "IS",
  ISR: "IL",
  ITA: "IT",
  JAM: "JM",
  JEY: "JE",
  JOR: "JO",
  JPN: "JP",
  KAZ: "KZ",
  KEN: "KE",
  KGZ: "KG",
  KHM: "KH",
  KIR: "KI",
  KNA: "KN",
  KOR: "KR",
  KWT: "KW",
  LAO: "LA",
  LBN: "LB",
  LBR: "LR",
  LBY: "LY",
  LCA: "LC",
  LIE: "LI",
  LKA: "LK",
  LSO: "LS",
  LTU: "LT",
  LUX: "LU",
  LVA: "LV",
  MAR: "MA",
  MCO: "MC",
  MDA: "MD",
  MDG: "MG",
  MDV: "MV",
  MEX: "MX",
  MHL: "MH",
  MKD: "MK",
  MLI: "ML",
  MLT: "MT",
  MMR: "MM",
  MNE: "ME",
  MNG: "MN",
  MOZ: "MZ",
  MRT: "MR",
  MSR: "MS",
  MUS: "MU",
  MWI: "MW",
  MYS: "MY",
  NAM: "NA",
  NER: "NE",
  NGA: "NG",
  NIC: "NI",
  NLD: "NL",
  NOR: "NO",
  NPL: "NP",
  NRU: "NR",
  NZL: "NZ",
  OMN: "OM",
  PAK: "PK",
  PAN: "PA",
  PER: "PE",
  PHL: "PH",
  PLW: "PW",
  PNG: "PG",
  POL: "PL",
  PRK: "KP",
  PRT: "PT",
  PRY: "PY",
  PSE: "PS",
  QAT: "QA",
  RKS: "XK",
  ROU: "RO",
  RUS: "RU",
  RWA: "RW",
  SAU: "SA",
  SDN: "SD",
  SEN: "SN",
  SGP: "SG",
  SHN: "SH",
  SLB: "SB",
  SLE: "SL",
  SLV: "SV",
  SMR: "SM",
  SOM: "SO",
  SRB: "RS",
  SSD: "SS",
  STP: "ST",
  SUR: "SR",
  SVK: "SK",
  SVN: "SI",
  SWE: "SE",
  SWZ: "SZ",
  SYC: "SC",
  SYR: "SY",
  TCA: "TC",
  TCD: "TD",
  TGO: "TG",
  THA: "TH",
  TJK: "TJ",
  TKM: "TM",
  TLS: "TL",
  TON: "TO",
  TTO: "TT",
  TUN: "TN",
  TUR: "TR",
  TUV: "TV",
  TWN: "TW",
  TZA: "TZ",
  UGA: "UG",
  UKR: "UA",
  URY: "UY",
  USA: "US",
  UZB: "UZ",
  VAT: "VA",
  VCT: "VC",
  VEN: "VE",
  VGB: "VG",
  VNM: "VN",
  VUT: "VU",
  WSM: "WS",
  YEM: "YE",
  ZAF: "ZA",
  ZMB: "ZM",
  ZWE: "ZW",
};

export const flagEmoji = (iso3Code) => {
  const iso2Code = ISO3_TO_ISO2[iso3Code];
  if (!iso2Code) return "";
  return iso2Code
    .toUpperCase()
    .replace(/./g, (char) => String.fromCodePoint(127397 + char.charCodeAt(0)));
};

// Feature flag for capacity report (libeen rules)
export const CAPACITY_REPORT_ENABLED = true;

// Set to true to skip actual Onfido SDK initialization (for UI testing)
export const MOCK_ONFIDO = false;

/**
 * Check if capacity report (libeen rules) should be shown
 * @param {Object} workspace - Current workspace object
 * @param {Object} user - Current user object
 * @returns {boolean} - True if capacity report should be shown
 */
export const shouldShowCapacityReport = (workspace = null, user = null) => {
  if (!CAPACITY_REPORT_ENABLED) {
    return false;
  }

  if (workspace?.capacity_report === true) {
    return true;
  }

  // Check if libeen rules are activated for this workspace
  const libeenRulesActivated = workspace?.libeen_rules_activated ?? false;

  return libeenRulesActivated;
};

export const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
